// The saved data: its shape, the starting data, and upgrades from older shapes.
// Everything is plain JSON so it can be synced, backed up, exported and merged.

import { isMonth, isDate } from './calendar.js';

export const SCHEMA = 4;

let counter = 0;
export const newId = (prefix = 'x') => `${prefix}${Date.now().toString(36)}${(counter++ % 1296).toString(36).padStart(2, '0')}${Math.random().toString(36).slice(2, 6)}`;

// Validated categorical order for the dark surface (colour-blind safe as adjacent pairs; first three as any pair)
export const PERSON_COLORS = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#9085e9'];

export const EXPENSE_CATEGORIES = ['Housing', 'Utilities', 'Groceries', 'Transport', 'Health', 'Insurance', 'Subscriptions', 'Leisure', 'Pets', 'Other'];

export function defaultPerson({ id, name, color, plan, irsJovem }) {
  return {
    id: id || newId('p'),
    name: name || 'Person',
    color: color || PERSON_COLORS[0],
    email: '',
    birthDate: '',
    since: null, // first month they take part (null: from the start)
    until: null, // last month they take part (null: still here)
    payday: { type: 'last-business-day' },
    holidays: { carnival: false, extra: [] },
    irsJovem: irsJovem || { enabled: false, firstYear: null, skipYears: [], atSource: true },
    personalDeductions: 250,
    plans: [plan],
    allowance: [], // personal spending money: [{ id, from, amount }]
  };
}

// What the app starts with: João's LAYER8 offer (2026 plan) and a partner to fill in
export function initialState(startMonth = '2026-10') {
  const joao = defaultPerson({
    id: 'p1',
    name: 'João',
    color: PERSON_COLORS[0],
    irsJovem: { enabled: true, firstYear: 2026, skipYears: [], atSource: true },
    plan: {
      id: 'plan1',
      from: startMonth,
      mode: 'employee',
      label: 'LAYER8 (RD)',
      base: 1200,
      commission: 500,
      mealPerDay: 10.46,
      mealType: 'card',
      allowance: 200,
      allowanceType: 'reimbursement',
      allowanceTaxable: false,
      allowanceLabel: 'Despesas (expense reimbursements)',
      subsidies: { mode: 'lump', holidayMonth: 6, christmasMonth: 12 },
      startDate: '',
      table: 'I',
      dependents: 0,
      ssRate: 11,
      weeklyDays: 5,
      vacationDays: 22,
      vacationMonth: 8,
      // As in the offer letter: social security only on the base salary, and a 3% UNA Seguros fee on commission
      commissionSS: false,
      yearlyBonus: 0,
      yearlyBonusMonth: 12,
      yearlyBonusLabel: 'Obj. Qual. (yearly bonus)',
      // The offer letter's IRS lines (normal IRS, no IRS Jovem): only used to compare with what you really get
      letterIrsBase: 133.45,
      letterIrsCommission: 56.05,
      letterNetYear: 20647.33,
      deductions: [
        { id: 'una', label: 'UNA Seguros', pct: 3, of: 'commission', fixed: 0, enabled: true },
      ],
    },
  });
  const partner = defaultPerson({
    id: 'p2',
    name: 'Bea',
    color: PERSON_COLORS[1],
    plan: { id: 'plan2', from: startMonth, mode: 'net', net: 0, mealCard: 0, fourteen: false, holidayMonth: 6, christmasMonth: 12 },
  });
  return {
    schema: SCHEMA,
    settings: {
      startMonth,
      horizon: 24,
      split: { mode: 'income', custom: {} },
      // Savings are money that can really be put aside: the meal card only pays for food, so it doesn't count
      countMealCard: false,
      countAllowance: true,
      irsRefund: { include: true, month: 5 },
      numberLocale: 'pt-PT',
      timezone: 'Europe/Lisbon',
      setupDone: [],
    },
    people: [joao, partner],
    expenses: [],
    months: {},
    savings: { opening: 0, openingNote: '', transactions: [], goals: [] },
    taxYears: {},
    log: [],
  };
}

const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

// Bring any saved data up to the current shape, filling in anything missing. Never throws.
export function migrate(input) {
  const base = initialState();
  if (!input || typeof input !== 'object') return base;
  const s = { ...input };
  if (!s.schema) s.schema = 1;
  if (s.schema < 2) {
    // 2: savings only count money that can really be put aside. The meal card stops counting, and the LAYER8
    // "Despesas" are expense reimbursements (money back for what was spent), not pay.
    s.settings = { ...obj(s.settings), countMealCard: false };
    s.people = arr(s.people).map((p) => (p && Array.isArray(p.plans) ? {
      ...p,
      plans: p.plans.map((pl) => (pl && pl.mode === 'employee' && !pl.allowanceType && /^despesas/i.test(pl.allowanceLabel || '')
        ? { ...pl, allowanceType: 'reimbursement', allowanceTaxable: false, allowanceLabel: 'Despesas (expense reimbursements)' } : pl)),
    } : p));
    s.schema = 2;
  }
  if (s.schema < 3) {
    // 3: the LAYER8 offer letter (October 2026): social security only on the base salary, and the 3% UNA Seguros fee
    // taken from commission
    s.people = arr(s.people).map((p) => (p && Array.isArray(p.plans) ? {
      ...p,
      plans: p.plans.map((pl) => (pl && pl.mode === 'employee' && /layer8/i.test(pl.label || '') && pl.commissionSS === undefined ? {
        ...pl,
        commissionSS: false,
        yearlyBonus: pl.yearlyBonus ?? 0,
        yearlyBonusMonth: pl.yearlyBonusMonth ?? 12,
        yearlyBonusLabel: pl.yearlyBonusLabel || 'Obj. Qual. (yearly bonus)',
        deductions: (pl.deductions || []).map((d) => (d && d.id === 'una' ? { ...d, label: 'UNA Seguros', enabled: true } : d)),
      } : pl)),
    } : p));
    s.schema = 3;
  }
  if (s.schema < 4) {
    // 4: keep the offer letter's own IRS figures (normal IRS) to compare with IRS Jovem on the Pay page
    s.people = arr(s.people).map((p) => (p && Array.isArray(p.plans) ? {
      ...p,
      plans: p.plans.map((pl) => (pl && pl.mode === 'employee' && /layer8/i.test(pl.label || '') && pl.base === 1200 && pl.commission === 500 && pl.letterIrsBase === undefined
        ? { ...pl, letterIrsBase: 133.45, letterIrsCommission: 56.05, letterNetYear: 20647.33 } : pl)),
    } : p));
    s.schema = 4;
  }
  // (future upgrades go here: if (s.schema < 5) { …; s.schema = 5; })
  s.schema = SCHEMA;
  s.settings = { ...base.settings, ...obj(s.settings) };
  s.settings.split = { ...base.settings.split, ...obj(s.settings.split) };
  s.settings.irsRefund = { ...base.settings.irsRefund, ...obj(s.settings.irsRefund) };
  if (!isMonth(s.settings.startMonth)) s.settings.startMonth = base.settings.startMonth;
  s.settings.setupDone = arr(s.settings.setupDone);
  s.people = arr(s.people).filter((p) => p && p.id).map((p) => ({
    ...defaultPerson({ id: p.id, name: p.name, color: p.color, plan: null }),
    ...p,
    plans: arr(p.plans).filter((x) => x && isMonth(x.from)).sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0)),
    allowance: arr(p.allowance).filter((x) => x && isMonth(x.from)).sort((a, b) => (a.from < b.from ? -1 : 1)),
    irsJovem: { enabled: false, firstYear: null, skipYears: [], atSource: true, ...obj(p.irsJovem) },
    holidays: { carnival: false, extra: [], ...obj(p.holidays) },
    payday: p.payday && p.payday.type ? p.payday : { type: 'last-business-day' },
    birthDate: isDate(p.birthDate) ? p.birthDate : '',
  }));
  if (!s.people.length) s.people = base.people;
  s.expenses = arr(s.expenses).filter((e) => e && e.id).map((e) => ({
    category: 'Other', owner: 'joint', every: 'month', spread: false, end: null, ...e,
    versions: arr(e.versions).filter((v) => v && isMonth(v.from)).sort((a, b) => (a.from < b.from ? -1 : 1)),
  })).filter((e) => e.versions.length);
  s.months = obj(s.months);
  s.savings = { ...base.savings, ...obj(s.savings) };
  s.savings.transactions = arr(s.savings.transactions).filter((t) => t && isDate(t.date) && Number.isFinite(t.amount));
  s.savings.goals = arr(s.savings.goals);
  s.taxYears = obj(s.taxYears);
  s.log = arr(s.log);
  return s;
}

// Basic check that an imported backup looks like our data
export function looksLikeState(x) {
  return !!(x && typeof x === 'object' && Array.isArray(x.people) && x.settings && typeof x.settings === 'object');
}

// The version of something (plan, allowance, expense amount) that applies in a month
export function versionAt(versions, month) {
  let found = null;
  for (const v of versions || []) if (v.from <= month) found = v;
  return found;
}

export const personActiveIn = (p, month, startMonth) => (p.since || startMonth) <= month && (!p.until || month <= p.until);

export const LOG_LIMIT = 300;
export function appendLog(state, entry) {
  const log = [...(state.log || []), { id: newId('l'), at: Date.now(), ...entry }];
  return { ...state, log: log.slice(-LOG_LIMIT) };
}
